import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { makePageT } from '../../shared/utils/pageI18n';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Megaphone, Search, Plus, RefreshCcw, TrendingUp, TrendingDown, Filter, Eye, X, XCircle, LayoutGrid, List, LayoutList, Upload, Download, FileSpreadsheet, FileText, ChevronDown, ChevronUp, CheckCircle, DollarSign, Package, Users, ShoppingCart, AlertCircle, Shield, Layers, Clock, Target, Award, Tag, Trash2 } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import FacturationListPageLayout, { facturationTableScrollClass } from '../facturation/components/FacturationListPageLayout';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { wrapControlsStripSearch } from '../../shared/utils/listPageSearchVisibility';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import AchatsFormModalTabs from './components/AchatsFormModalTabs';
import { mapAchatsStatCards, buildAchatsImportExportProps, buildAchatsColumnDefs, achatsColHeaderProps, ACHATS_FORM_MODAL_SHELL, ACHATS_FORM_TAB_PANEL, ACHATS_FORM_GRID, useAchatsModalTheme, useAchatsColumnVisibility, achatsTableColSpan, ACHATS_TABLE_ACTIONS_TH_CLASS, useAchatsRowSelection, buildAchatsBulkDeleteFilterAction, AchatsBulkDeleteBar, useAppNotificationToast, AchatsCrudNotificationToast, sortRecordsNewestFirst, notifyCreated, notifyUpdated, notifyDeleted, notifyBulkDeleted, notifyError, resolveAchatsEntityReference, AchatsConfigNotice, useAchatsListViewMode, useAchatsListDisplay, achatsSettingsFromConfig } from './achatsListPageUtils';
import { useAchatsConfig } from './useAchatsConfig';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import '../../shared/components/custom/CustomComponents.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

const AppelsOffresPage = () => {
  const { t, i18n } = useTranslation();
  const p = makePageT(t, 'modules.achats.pages.appelsOffresPage');
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const { cfgTheme, modal: m } = useAchatsModalTheme();
  const achatsConfig = useAchatsConfig();
  const achatsSettings = achatsSettingsFromConfig(achatsConfig);
  const dateLocale = getBrandLocale(i18n.language);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(dateLocale) : '-';

  const [searchTerm, setSearchTerm] = useState('');
  const [tenders, setTenders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTender, setEditingTender] = useState(null);
  const [selectedTenders, setSelectedTenders] = useState([]);
  const [viewMode, setViewMode] = useAchatsListViewMode(achatsConfig);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingTender, setViewingTender] = useState(null);
  
  // Collapsible sections state
  const [formTab, setFormTab] = useState('general');
  const formTabs = useMemo(() => ([
    { id: 'general', label: p('section_basic_info'), icon: FileText, iconColor: 'text-blue-400' },
    { id: 'budget', label: p('section_budget'), icon: DollarSign, iconColor: 'text-green-400' },
    { id: 'requirements', label: p('section_requirements_docs'), icon: FileText, iconColor: 'text-amber-400' },
  ]), [p]);
  const [formData, setFormData] = useState({ 
    tender_reference: '', 
    tender_name: '',
    tender_type: 'open',
    status: 'draft',
    publication_date: new Date().toISOString().split('T')[0], 
    closing_date: '', 
    opening_date: '',
    estimated_budget: '', 
    minimum_budget: '',
    maximum_budget: '',
    awarded_amount: '',
    category: '',
    priority: 'medium',
    department: '',
    responsible_person: '',
    bidders_count: '',
    winner_name: '',
    winner_amount: '',
    description: '',
    requirements: '',
    terms_conditions: '',
    evaluation_criteria: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [statusFilter, setStatusFilter] = useState('all');
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_tenders');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { loadTenders(); }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadTenders = async () => {
    try {
      const response = await localApiAdapter.get('/achats/appels-offres');
      setTenders(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading tenders:', error);
      setTenders([]);
    }
  };

  const stats = {
    total: tenders.length,
    published: tenders.filter(t => t.status === 'published').length,
    closed: tenders.filter(t => t.status === 'closed').length,
    awarded: tenders.filter(t => t.status === 'awarded').length,
    totalBudget: tenders.reduce((sum, t) => sum + (parseFloat(t.estimated_budget) || 0), 0),
    awardedAmount: tenders.reduce((sum, t) => sum + (parseFloat(t.awarded_amount) || 0), 0)
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_tenders', name: p('mainTab_general'), icon: LayoutGrid },
    { id: 'par_type', name: p('mainTab_by_type'), icon: Shield },
    { id: 'par_statut', name: p('mainTab_by_status'), icon: Target },
  ]), [t]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: p('quickFilter_all'), icon: Layers },
    { id: 'published', name: p('quickFilter_published'), icon: Megaphone },
    { id: 'closed', name: p('quickFilter_closed'), icon: XCircle },
    { id: 'awarded', name: p('quickFilter_awarded'), icon: Award },
  ]), [t]);

  const tenderDistribution = useMemo(() => ({
    all_tenders: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: 'published', name: p('dist_published'), count: stats.published, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'closed', name: p('dist_closed'), count: stats.closed, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'awarded', name: p('dist_awarded'), count: stats.awarded, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    par_type: {
      title: p('dist_by_type_title'),
      data: [
        { filterKey: 'open', name: p('dist_open'), count: tenders.filter(t => t.tender_type === 'open').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'restricted', name: p('dist_restricted'), count: tenders.filter(t => t.tender_type === 'restricted').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'negotiated', name: p('dist_negotiated'), count: tenders.filter(t => t.tender_type === 'negotiated').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'framework', name: p('dist_framework'), count: tenders.filter(t => t.tender_type === 'framework').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: 'published', name: p('dist_published'), count: stats.published, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'closed', name: p('dist_in_progress'), count: stats.closed, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'awarded', name: p('dist_awarded'), count: stats.awarded, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
  }), [t, stats.published, stats.closed, stats.awarded, tenders]);

  const distributionInfo = tenderDistribution[mainViewMode] || tenderDistribution.all_tenders;

  const baseFilteredTenders = tenders.filter(t => {
    // Date range filter
    if (dateRange.start || dateRange.end) {
      const tenderDate = new Date(t.publication_date);
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (tenderDate < startOfDay) return false;
      }
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (tenderDate > endOfDay) return false;
      }
    }
    
    // Search filter
    if (searchTerm && !t.tender_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !t.tender_reference?.toLowerCase().includes(searchTerm.toLowerCase())) return false;

    if (qawaem && statusFilter !== 'all' && t.status !== statusFilter) return false;
    
    // Quick filter (only for Vue Générale)
    if (mainViewMode === 'all_tenders') {
      if (quickFilter === 'published' && t.status !== 'published') return false;
      if (quickFilter === 'closed' && t.status !== 'closed') return false;
      if (quickFilter === 'awarded' && t.status !== 'awarded') return false;
    }
    
    // Distribution card filter (clickable stats)
    if (activeDistributionFilter) {
      if (mainViewMode === 'all_tenders' || mainViewMode === 'par_statut') {
        if (t.status !== activeDistributionFilter) return false;
      } else if (mainViewMode === 'par_type') {
        if (t.tender_type !== activeDistributionFilter) return false;
      }
    }
    
    return true;
  });

  const generateTenderReference = useCallback(
    () => resolveAchatsEntityReference(achatsSettings, 'tender', 'AO', tenders, (t) => t.tender_reference),
    [achatsSettings, tenders],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const tenderReference = resolveAchatsEntityReference(
        achatsSettings,
        'tender',
        'AO',
        tenders,
        (t) => t.tender_reference,
        formData.tender_reference,
      );
      const submitData = { 
        tender_reference: tenderReference,
        tender_name: formData.tender_name,
        tender_type: formData.tender_type,
        status: formData.status,
        publication_date: formData.publication_date,
        closing_date: formData.closing_date,
        opening_date: formData.opening_date,
        estimated_budget: parseFloat(formData.estimated_budget) || 0,
        minimum_budget: parseFloat(formData.minimum_budget) || 0,
        maximum_budget: parseFloat(formData.maximum_budget) || 0,
        awarded_amount: parseFloat(formData.awarded_amount) || 0,
        category: formData.category,
        priority: formData.priority,
        department: formData.department,
        responsible_person: formData.responsible_person,
        bidders_count: parseInt(formData.bidders_count) || 0,
        winner_name: formData.winner_name,
        winner_amount: parseFloat(formData.winner_amount) || 0,
        description: formData.description,
        requirements: formData.requirements,
        terms_conditions: formData.terms_conditions,
        evaluation_criteria: formData.evaluation_criteria
      };
      if (editingTender) {
        await localApiAdapter.put(`/achats/appels-offres/${editingTender.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/achats/appels-offres', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error') + (error.message || p('unknown_error')));
    } finally {
      resetForm();
      loadTenders();
    }
  };

  const handleEdit = (tender) => {
    setEditingTender(tender);
    setFormData({ 
      tender_reference: tender.tender_reference || '',
      tender_name: tender.tender_name || '',
      tender_type: tender.tender_type || 'open',
      status: tender.status || 'draft',
      publication_date: tender.publication_date || '',
      closing_date: tender.closing_date || '',
      opening_date: tender.opening_date || '',
      estimated_budget: tender.estimated_budget?.toString() || '',
      minimum_budget: tender.minimum_budget?.toString() || '',
      maximum_budget: tender.maximum_budget?.toString() || '',
      awarded_amount: tender.awarded_amount?.toString() || '',
      category: tender.category || '',
      priority: tender.priority || 'medium',
      department: tender.department || '',
      responsible_person: tender.responsible_person || '',
      bidders_count: tender.bidders_count?.toString() || '',
      winner_name: tender.winner_name || '',
      winner_amount: tender.winner_amount?.toString() || '',
      description: tender.description || '',
      requirements: tender.requirements || '',
      terms_conditions: tender.terms_conditions || '',
      evaluation_criteria: tender.evaluation_criteria || ''
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/achats/appels-offres/${id}`);
        notifyDeleted(showNotify, entityLabel);
        loadTenders();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('save_error', { defaultValue: p('unknown_error') }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedTenders.length }))) {
      try {
        const deleteCount = selectedTenders.length;
        await Promise.all(selectedTenders.map(id => localApiAdapter.delete(`/achats/appels-offres/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabel);
        setSelectedTenders([]);
        loadTenders();
      } catch (error) {
        console.error('Error deleting tenders:', error);
        notifyError(showNotify, p('save_error', { defaultValue: p('unknown_error') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      tender_reference: '', 
      tender_name: '',
      tender_type: 'open',
      status: 'draft',
      publication_date: '', 
      closing_date: '', 
      opening_date: '',
      estimated_budget: '', 
      minimum_budget: '',
      maximum_budget: '',
      awarded_amount: '',
      category: '',
      priority: 'medium',
      department: '',
      responsible_person: '',
      bidders_count: '',
      winner_name: '',
      winner_amount: '',
      description: '',
      requirements: '',
      terms_conditions: '',
      evaluation_criteria: ''
    });
    setEditingTender(null);
    setFormTab('general');
    setShowModal(false);
  };

  const handleExport = () => {
    const headers = [
      { key: 'tender_reference', label: p('col_reference') },
      { key: 'tender_name', label: p('col_name') },
      { key: 'tender_type', label: p('col_type') },
      { key: 'status', label: p('col_status') },
      { key: 'publication_date', label: p('label_publication_date') },
      { key: 'closing_date', label: p('label_closing_date') },
      { key: 'estimated_budget', label: p('label_estimated_budget') },
      { key: 'priority', label: p('col_priority') }
    ];
    const fileName = `appels_offres_${new Date().toISOString().split('T')[0]}`;
    exportToExcel(filteredTenders, headers, `${fileName}.xlsx`);
    setShowExportDropdown(false);
  };

  const handleExportCSV = () => {
    const headers = [
      { key: 'tender_reference', label: p('col_reference') },
      { key: 'tender_name', label: p('col_name') },
      { key: 'tender_type', label: p('col_type') },
      { key: 'status', label: p('col_status') },
      { key: 'publication_date', label: p('label_publication_date') },
      { key: 'estimated_budget', label: p('label_estimated_budget') },
    ];
    const fileName = `appels_offres_${new Date().toISOString().split('T')[0]}`;
    exportToCSV(filteredTenders, headers, `${fileName}.csv`);
    setShowExportDropdown(false);
  };

  const handleImport = () => {
    const headers = [
      { key: 'tender_reference', label: p('col_reference') },
      { key: 'tender_name', label: p('col_name') },
      { key: 'tender_type', label: p('col_type') },
      { key: 'status', label: p('col_status') },
      { key: 'publication_date', label: p('label_publication_date') },
      { key: 'estimated_budget', label: p('label_estimated_budget') }
    ];
    const schema = {
      requiredFields: ['tender_reference', 'tender_name'],
      validators: {
        estimated_budget: (value) => !value || !isNaN(parseFloat(value))
      }
    };
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/achats/appels-offres', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          await loadTenders();
          alert(p('import_success', { total: result.totalCount, imported: successCount }));
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };


  const handleSelectAll = () => {
    if (rowSelection.allRowsSelected) {
      setSelectedTenders([]);
    } else {
      setSelectedTenders(filteredTenders.map((r) => r.id));
    }
  };
  
  const handleSelectTender = (id) => { 
    setSelectedTenders(selectedTenders.includes(id) ? selectedTenders.filter(tId => tId !== id) : [...selectedTenders, id]); 
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/achats/appels-offres/${recordId}`, { status: newStatus });
      await loadTenders();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find((s) => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>{Icon ? <Icon size={14} /> : null}{label}</span>;
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { label: p('priority_low'), style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
      medium: { label: p('priority_medium'), style: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
      high: { label: p('priority_high'), style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
      critical: { label: p('priority_critical'), style: 'bg-red-500/20 text-red-300 border-red-500/30' },
    };
    const config = priorityConfig[priority] || priorityConfig.medium;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${config.style}`}>{config.label}</span>;
  };

  const tenderTypeOptions = useMemo(() => ([
    { value: 'open', label: p('type_open') },
    { value: 'restricted', label: p('type_restricted') },
    { value: 'negotiated', label: p('type_negotiated') },
    { value: 'framework', label: p('type_framework') },
  ]), [t]);

  const statusOptions = useMemo(() => ([
    { value: 'draft', label: p('status_draft'), icon: FileText, style: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
    { value: 'published', label: p('status_published'), icon: Megaphone, style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
    { value: 'closed', label: p('status_closed'), icon: Clock, style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
    { value: 'awarded', label: p('status_awarded'), icon: Award, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'cancelled', label: p('status_cancelled'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
  ]), [p]);

  const priorityOptions = useMemo(() => ([
    { value: 'low', label: p('priority_low') },
    { value: 'medium', label: p('priority_medium') },
    { value: 'high', label: p('priority_high') },
    { value: 'critical', label: p('priority_critical') },
  ]), [t]);

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('tender_reference'),
      textColumnRule('tender_name'),
      exactColumnRule('tender_type', (row) => row.tender_type || ''),
      textColumnRule('publication_date', (row) => fmtDate(row.publication_date)),
      textColumnRule('estimated_budget', (row) => String(row.estimated_budget ?? '')),
      textColumnRule('bidders_count', (row) => String(row.bidders_count ?? '')),
      exactColumnRule('priority', (row) => row.priority || ''),
      exactColumnRule('status', (row) => row.status || ''),
    ],
    [i18n.language],
  );

  const { filtered: filteredTenders, displayed: displayedTenders } = useAchatsListDisplay(
    baseFilteredTenders,
    colFilters,
    columnFilterRules,
    achatsConfig.itemsPerPage,
  );

  const rowSelection = useAchatsRowSelection(selectedTenders, filteredTenders);

  const colHeader = (columnKey, label, opts = {}) =>
    achatsColHeaderProps({ columnKey, label, colFilters, setColFilter, editingKey, setEditingKey, ...opts });

  const categoryOptions = useMemo(() => ([
    { value: 'travaux', label: p('category_travaux') },
    { value: 'fournitures', label: p('category_fournitures') },
    { value: 'services', label: p('category_services') },
    { value: 'etudes', label: p('category_etudes') },
  ]), [t]);

  const appelsColumnKeys = useMemo(
    () => ['tender_reference', 'tender_name', 'tender_type', 'publication_date', 'estimated_budget', 'bidders_count', 'priority', 'status', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useAchatsColumnVisibility(appelsColumnKeys);

  const qawaemColumns = useMemo(
    () => buildAchatsColumnDefs([
      { key: 'tender_reference', labelKey: 'col_reference', required: true },
      { key: 'tender_name', labelKey: 'col_name' },
      { key: 'tender_type', labelKey: 'col_type' },
      { key: 'publication_date', labelKey: 'col_publication' },
      { key: 'estimated_budget', labelKey: 'col_budget' },
      { key: 'bidders_count', labelKey: 'col_bidders' },
      { key: 'priority', labelKey: 'col_priority' },
      { key: 'status', labelKey: 'col_status' },
      { key: 'actions', labelKey: 'col_actions', required: true },
    ], p, columnVisibility),
    [p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = achatsTableColSpan(qawaemColumns);

  const listStats = useMemo(
    () => mapAchatsStatCards([
      { key: 'total', label: p('stat_total'), value: stats.total, icon: Megaphone, variant: 'copper' },
      { key: 'published', label: p('stat_published'), value: stats.published, icon: CheckCircle, variant: 'forest' },
      { key: 'closed', label: p('stat_closed'), value: stats.closed, icon: XCircle, variant: 'copper' },
      { key: 'awarded', label: p('stat_awarded'), value: stats.awarded, icon: Award, variant: 'forest' },
    ]),
    [stats, p],
  );

  const importExportProps = useMemo(
    () => buildAchatsImportExportProps({
      showImportDropdown,
      setShowImportDropdown,
      showExportDropdown,
      setShowExportDropdown,
      importDropdownRef,
      exportDropdownRef,
      importLabel: t('common.import'),
      exportLabel: t('common.export'),
      generatingLabel: t('common.loading'),
      handlers: {
        onImport: handleImport,
        onExportCSV: handleExportCSV,
        onExportExcel: handleExport,
      },
    }),
    [showImportDropdown, showExportDropdown, t],
  );

  const advancedOptionsContent = (
    <div className="space-y-6 pt-6">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setMainViewMode(tab.id);
                setQuickFilter('all');
                setActiveDistributionFilter(null);
              }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                mainViewMode === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {mainViewMode === 'all_tenders' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setQuickFilter(filter.id)}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  quickFilter === filter.id
                    ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <filter.icon className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
          {activeDistributionFilter && (
            <button
              onClick={() => setActiveDistributionFilter(null)}
              className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
            >
              <XCircle size={14} />
              {p('reset_filter')}
            </button>
          )}
        </div>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3', 'lg:grid-cols-4')}>
          {distributionInfo.data.map((item) => (
            <button
              key={item.filterKey || item.name}
              onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))}
              className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${
                activeDistributionFilter === item.name
                  ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20'
                  : 'border-white/10 hover:border-white/20 hover:bg-white/10'
              }`}
              title={p('filter_by_title', { name: item.name })}
            >
              <div className="flex justify-between items-center mb-2">
                <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>
                  {item.name}
                </h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>
                  {item.count}
                </div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                <div
                  className={`h-3 rounded-full ${item.color} transition-all`}
                  style={{
                    width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%`,
                  }}
                />
              </div>
              <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>
                {(() => {
                  const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                  return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                })()}{p('percent_of_tenders')}
              </p>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );

  return (
    <>
      <AchatsConfigNotice
        message={!achatsConfig.enableTenders ? p('notice_tenders_disabled', { defaultValue: 'المناقصات معطّلة — فعّلها من إعدادات المشتريات ← المناقصات والعروض.' }) : null}
      />
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Megaphone}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildAchatsBulkDeleteFilterAction(selectedTenders.length, handleBulkDelete, { deleteLabel: `${t('common.delete')} (${selectedTenders.length})`, title: p('bulk_delete_confirm', { count: selectedTenders.length }) })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search') })}
        onCreate={() => {
          if (!achatsConfig.enableTenders) {
            alert(p('feature_disabled', { defaultValue: 'المناقصات معطّلة من إعدادات المشتريات.' }));
            return;
          }
          setEditingTender(null);
          setFormData({
            tender_reference: generateTenderReference(),
            tender_name: '',
            tender_type: 'open',
            status: 'draft',
            publication_date: new Date().toISOString().split('T')[0],
            closing_date: '',
            opening_date: '',
            estimated_budget: '',
            minimum_budget: '',
            maximum_budget: '',
            awarded_amount: '',
            category: '',
            priority: 'medium',
            department: '',
            responsible_person: '',
            bidders_count: '',
            winner_name: '',
            winner_amount: '',
            description: '',
            requirements: '',
            terms_conditions: '',
            evaluation_criteria: '',
          });
          setFormTab('general');
          setShowModal(true);
        }}
        createLabel={p('new_tender')}
        importExport={importExportProps}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={
          !qawaem ? (
            <div className="facturation-list-controls-strip p-4 border-b border-white/10">
              <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
                {wrapControlsStripSearch(viewMode, (
                  <div className="relative min-w-0 flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      placeholder={p('search_placeholder', { defaultValue: t('common.search') })}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
                <FacturationViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  isLight={isDashboardLightMode}
                  cardsTitle={t('common.view_cards')}
                  tableTitle={t('common.view_table')}
                />
              </div>
            </div>
          ) : null
        }
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={rowSelection.allRowsSelected}
                        indeterminate={rowSelection.someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  
                  {isColumnVisible('tender_reference') ? <TableColumnHeaderFilter {...colHeader('tender_reference', p('col_reference'))} /> : null}
                  {isColumnVisible('tender_name') ? <TableColumnHeaderFilter {...colHeader('tender_name', p('col_name'))} /> : null}
                  {isColumnVisible('tender_type') ? <TableColumnHeaderFilter {...colHeader('tender_type', p('col_type'), { kind: 'select', options: tenderTypeOptions })} /> : null}
                  {isColumnVisible('publication_date') ? <TableColumnHeaderFilter {...colHeader('publication_date', p('col_publication'))} /> : null}
                  {isColumnVisible('estimated_budget') ? <TableColumnHeaderFilter {...colHeader('estimated_budget', p('col_budget'), { align: 'center' })} /> : null}
                  {isColumnVisible('bidders_count') ? <TableColumnHeaderFilter {...colHeader('bidders_count', p('col_bidders'), { align: 'center' })} /> : null}
                  {isColumnVisible('priority') ? <TableColumnHeaderFilter {...colHeader('priority', p('col_priority'), { kind: 'select', options: priorityOptions, align: 'center' })} /> : null}
                  {isColumnVisible('status') ? <TableColumnHeaderFilter {...colHeader('status', p('col_status'), { kind: 'select', options: statusOptions, align: 'center' })} /> : null}
                  <QawaemTableHeaderActionsCell
                    thClassName={ACHATS_TABLE_ACTIONS_TH_CLASS}
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedTenders.length > 0 ? (
                  displayedTenders.map((tender, index) => (
                    <tr 
                      key={tender.id} 
                      className={`hover:bg-white/5 transition-colors ${selectedTenders.includes(tender.id) ? 'bg-white/10' : ''}`}
                    >
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedTenders.includes(tender.id)}
                              onChange={() => handleSelectTender(tender.id)}
                            />
                          </div>
                        </td>
                      {isColumnVisible('tender_reference') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{tender.tender_reference}</span>
                      </td>
                      ) : null}
                      {isColumnVisible('tender_name') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{tender.tender_name}</span>
                          <span className="text-white/50 text-xs mt-0.5">{tender.category || p('uncategorized')}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColumnVisible('tender_type') ? (
                      <td className="px-6 py-4">
                        <span className="text-white/80">{tenderTypeOptions.find(t => t.value === tender.tender_type)?.label || tender.tender_type}</span>
                      </td>
                      ) : null}
                      {isColumnVisible('publication_date') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{fmtDate(tender.publication_date)}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {p('closing_label')} {fmtDate(tender.closing_date)}
                          </span>
                        </div>
                      </td>
                      ) : null}
                      {isColumnVisible('estimated_budget') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{formatBrandMoney((tender.estimated_budget || 0))}</span>
                      </td>
                      ) : null}
                      {isColumnVisible('bidders_count') ? (
                      <td className="px-6 py-4 text-center">
                        <span className="text-blue-300 font-semibold">{tender.bidders_count || 0}</span>
                      </td>
                      ) : null}
                      {isColumnVisible('priority') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">{getPriorityBadge(tender.priority)}</div>
                      </td>
                      ) : null}
                      {isColumnVisible('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge status={tender.status} recordId={tender.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingTender(tender); setShowViewModal(true); }}
                          onEdit={() => handleEdit(tender)}
                          onDelete={() => handleDelete(tender.id)}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Megaphone size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Card View */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedTenders.length > 0 ? (
              displayedTenders.map((tender, index) => (
                <motion.div
                  key={tender.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Megaphone size={20} className="text-blue-400" />
                          {tender.tender_name}
                        </h3>
                        <div className="flex gap-2">
                          <QawaemClickableStatusBadge status={tender.status} recordId={tender.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                          {getPriorityBadge(tender.priority)}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('col_reference')}:</span> {tender.tender_reference}</div>
                            <div><span className="font-medium">{p('card_type')}</span> {tenderTypeOptions.find(t => t.value === tender.tender_type)?.label}</div>
                            <div><span className="font-medium">{p('card_category')}</span> {tender.category || p('uncategorized')}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_dates')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_publication')}</span> {fmtDate(tender.publication_date)}</div>
                            <div><span className="font-medium">{p('closing_label')}</span> {fmtDate(tender.closing_date)}</div>
                            <div><span className="font-medium">{p('card_opening')}</span> {fmtDate(tender.opening_date)}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_budget')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_estimated')}</span> {formatBrandMoney((tender.estimated_budget || 0))}</div>
                            <div><span className="font-medium">{p('card_awarded')}</span> <span className="text-green-300">{formatBrandMoney((tender.awarded_amount || 0))}</span></div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_submissions')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_count')}</span> {tender.bidders_count || 0}</div>
                            {tender.winner_name && <div><span className="font-medium">{p('card_winner')}</span> {tender.winner_name}</div>}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingTender(tender); setShowViewModal(true); }}
                      onEdit={() => handleEdit(tender)}
                      onDelete={() => handleDelete(tender.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Megaphone size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty')}</p>
              </div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {/* View Modal */}
      {showViewModal && viewingTender ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_modal_title')}
        icon={Eye}
        testId="achats-view-modal"
      >
        <div className="space-y-4">
                <div className={m.viewSection}>
                  <h3 className={m.viewSectionTitle}>{p('section_general_info')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className={m.viewLabel}>{p('col_reference')}</p>
                      <p className={m.viewValue}>{viewingTender.tender_reference}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_name')}</p>
                      <p className={m.viewValue}>{viewingTender.tender_name}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('col_type')}</p>
                      <p className={m.viewValue}>{tenderTypeOptions.find(t => t.value === viewingTender.tender_type)?.label}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_status')}</p>
                      <div className="flex">{getStatusBadge(viewingTender.status)}</div>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_priority')}</p>
                      <div className="flex">{getPriorityBadge(viewingTender.priority)}</div>
                    </div>
                  </div>
                </div>

                {viewingTender.description && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('label_description')}</h3>
                    <p className="text-white/80">{viewingTender.description}</p>
                  </div>
                )}

                {viewingTender.requirements && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('section_requirements')}</h3>
                    <p className="text-white/80 whitespace-pre-wrap">{viewingTender.requirements}</p>
                  </div>
                )}

                {viewingTender.terms_conditions && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('section_terms')}</h3>
                    <p className="text-white/80 whitespace-pre-wrap">{viewingTender.terms_conditions}</p>
                  </div>
                )}

                {viewingTender.evaluation_criteria && (
                  <div className={m.viewSection}>
                    <h3 className={`${m.viewSectionTitle} mb-2`}>{p('section_evaluation')}</h3>
                    <p className="text-white/80 whitespace-pre-wrap">{viewingTender.evaluation_criteria}</p>
                  </div>
                )}
        </div>
      </QawaemErpViewModal>
      ) : null}

      {/* Edit/Create Modal - WITH CUSTOM COMPONENTS */}
      <QawaemErpCrudModal
        open={showModal}
        onClose={() => resetForm()}
        title={editingTender ? p('modal_edit') : p('modal_new')}
        icon={FileText}
        formId="tender-form"
        saveLabel={t('common.save')}
        onSubmit
        testId="achats-crud-modal"
      >
        <div className={ACHATS_FORM_MODAL_SHELL}>
          <AchatsFormModalTabs
            tabs={formTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            isLight={m.isLight}
            ariaLabel={editingTender ? p('modal_edit') : p('modal_new')}
          />
          <div className={ACHATS_FORM_TAB_PANEL}>
            <form id="tender-form" onSubmit={handleSubmit}>
                {formTab === 'general' && (
                  <div className={ACHATS_FORM_GRID}>
                    <div>
                      <label className={cfgTheme.label}><RequiredLabelText text={p('label_reference_required')} /></label>
                      <input
                        type="text"
                        required
                        value={formData.tender_reference}
                        onChange={(e) => setFormData({ ...formData, tender_reference: e.target.value })}
                        className={cfgTheme.input}
                        placeholder={p('placeholder_ref')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}><RequiredLabelText text={p('label_tender_name_required')} /></label>
                      <input
                        type="text"
                        required
                        value={formData.tender_name}
                        onChange={(e) => setFormData({ ...formData, tender_name: e.target.value })}
                        className={cfgTheme.input}
                        placeholder={p('placeholder_name')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}><RequiredLabelText text={p('label_tender_type_required')} /></label>
                      <CustomSelect
                        value={formData.tender_type}
                        onChange={(value) => setFormData({ ...formData, tender_type: value || 'open' })}
                        options={tenderTypeOptions}
                        placeholder={p('select')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}><RequiredLabelText text={p('label_status_required')} /></label>
                      <CustomSelect
                        value={formData.status}
                        onChange={(value) => setFormData({ ...formData, status: value || 'draft' })}
                        options={statusOptions}
                        placeholder={p('select')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}><RequiredLabelText text={p('label_priority_required')} /></label>
                      <CustomSelect
                        value={formData.priority}
                        onChange={(value) => setFormData({ ...formData, priority: value || 'medium' })}
                        options={priorityOptions}
                        placeholder={p('select')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_category')}</label>
                      <CustomSelect
                        value={formData.category}
                        onChange={(value) => setFormData({ ...formData, category: value || '' })}
                        options={categoryOptions}
                        placeholder={p('select_or_type')}
                        searchable
                        creatable
                        autoSelect={false}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_department')}</label>
                      <input
                        type="text"
                        value={formData.department}
                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                        className={cfgTheme.input}
                        placeholder={p('placeholder_department')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_responsible')}</label>
                      <input
                        type="text"
                        value={formData.responsible_person}
                        onChange={(e) => setFormData({ ...formData, responsible_person: e.target.value })}
                        className={cfgTheme.input}
                        placeholder={p('placeholder_responsible')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_publication_date')}</label>
                      <CustomDatePicker
                        value={formData.publication_date}
                        onChange={(date) => setFormData({ ...formData, publication_date: date })}
                        placeholder={p('select_date')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_closing_date')}</label>
                      <CustomDatePicker
                        value={formData.closing_date}
                        onChange={(date) => setFormData({ ...formData, closing_date: date })}
                        placeholder={p('select_date')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_opening_date')}</label>
                      <CustomDatePicker
                        value={formData.opening_date}
                        onChange={(date) => setFormData({ ...formData, opening_date: date })}
                        placeholder={p('select_date')}
                      />
                    </div>
                  </div>
                )}

                {formTab === 'budget' && (
                  <div className={ACHATS_FORM_GRID}>
                    <div>
                      <label className={cfgTheme.label}>{p('label_estimated_budget')}</label>
                      <CustomNumberInput
                        value={formData.estimated_budget}
                        onChange={(value) => setFormData({ ...formData, estimated_budget: value })}
                        placeholder="0.00"
                        step={1000}
                        min={0}
                        decimals={2}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_min_budget')}</label>
                      <CustomNumberInput
                        value={formData.minimum_budget}
                        onChange={(value) => setFormData({ ...formData, minimum_budget: value })}
                        placeholder="0.00"
                        step={1000}
                        min={0}
                        decimals={2}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_max_budget')}</label>
                      <CustomNumberInput
                        value={formData.maximum_budget}
                        onChange={(value) => setFormData({ ...formData, maximum_budget: value })}
                        placeholder="0.00"
                        step={1000}
                        min={0}
                        decimals={2}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_awarded_amount')}</label>
                      <CustomNumberInput
                        value={formData.awarded_amount}
                        onChange={(value) => setFormData({ ...formData, awarded_amount: value })}
                        placeholder="0.00"
                        step={1000}
                        min={0}
                        decimals={2}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_bidders_count')}</label>
                      <input
                        type="number"
                        min={0}
                        value={formData.bidders_count}
                        onChange={(e) => setFormData({ ...formData, bidders_count: e.target.value })}
                        className={cfgTheme.input}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_winner')}</label>
                      <input
                        type="text"
                        value={formData.winner_name}
                        onChange={(e) => setFormData({ ...formData, winner_name: e.target.value })}
                        className={cfgTheme.input}
                        placeholder={p('placeholder_winner')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('label_amount')}</label>
                      <CustomNumberInput
                        value={formData.winner_amount}
                        onChange={(value) => setFormData({ ...formData, winner_amount: value })}
                        placeholder="0.00"
                        step={1000}
                        min={0}
                        decimals={2}
                      />
                    </div>
                  </div>
                )}

                {formTab === 'requirements' && (
                  <div className={ACHATS_FORM_GRID}>
                    <div className="md:col-span-3">
                      <label className={cfgTheme.label}>{p('label_description')}</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[100px]"
                        placeholder={p('placeholder_description')}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className={cfgTheme.label}>{p('label_requirements')}</label>
                      <textarea
                        value={formData.requirements}
                        onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[100px]"
                        placeholder={p('placeholder_requirements')}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className={cfgTheme.label}>{p('label_terms')}</label>
                      <textarea
                        value={formData.terms_conditions}
                        onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[100px]"
                        placeholder={p('placeholder_terms')}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className={cfgTheme.label}>{p('label_evaluation_criteria')}</label>
                      <textarea
                        value={formData.evaluation_criteria}
                        onChange={(e) => setFormData({ ...formData, evaluation_criteria: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all placeholder-white/30 min-h-[100px]"
                        placeholder={p('placeholder_evaluation')}
                      />
                    </div>
                  </div>
                )}

            </form>
          </div>
        </div>
      </QawaemErpCrudModal>
      <AchatsCrudNotificationToast notifyToast={notifyToast} onDismiss={dismissNotify} />
    </>
  );
};

export default AppelsOffresPage;

